import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { buildApiUrl } from "../helpers/base_url";

const VerifyEmail: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isResending, setIsResending] = useState(false);
  const [status, setStatus] = useState<{
    type: "idle" | "success" | "error";
    message: string;
  }>({ type: "idle", message: "" });

  const verifyEmail = useCallback(
    async (t: string, e: string) => {
    setIsVerifying(true);
    setStatus({ type: "idle", message: "" });

    try {
      const response = await fetch(
        buildApiUrl(
          `/api/email/verify?token=${encodeURIComponent(t)}&email=${encodeURIComponent(e)}`
        ),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json().catch(() => null);

      if (response.ok && data?.success) {
        setStatus({
          type: "success",
          message:
            data?.data ||
            "Email verified successfully! You can now log in to your account.",
        });
        // Auto-redirect to login after 2.5 seconds
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 2500);
      } else {
        setStatus({
          type: "error",
          message:
            data?.message ||
            "Verification failed. The link may be invalid or expired.",
        });
      }
    } catch (error) {
      setStatus({
        type: "error",
        message: "Network error. Please check your connection and try again.",
      });
    } finally {
      setIsVerifying(false);
    }
    },
    [navigate]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("token");
    const e = params.get("email");

    setEmail(e);

    if (!t || !e) {
      setIsVerifying(false);
      setStatus({
        type: "error",
        message: "Invalid verification link. Please check your email and try again.",
      });
      return;
    }

    // Automatically verify on mount
    verifyEmail(t, e);
  }, [location.search, verifyEmail]);

  const handleResendVerification = async () => {
    if (!email) {
      setStatus({
        type: "error",
        message: "Email address is required to resend verification.",
      });
      return;
    }

    setIsResending(true);
    setStatus({ type: "idle", message: "" });

    try {
      const response = await fetch(
        buildApiUrl(
          `/api/email/resend-verification?email=${encodeURIComponent(email)}`
        ),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json().catch(() => null);

      if (response.ok && data?.success) {
        setStatus({
          type: "success",
          message:
            data?.message ||
            "A new verification email has been sent. Please check your inbox.",
        });
      } else {
        setStatus({
          type: "error",
          message:
            data?.message ||
            "Failed to resend verification email. Please try again later.",
        });
      }
    } catch (error) {
      setStatus({
        type: "error",
        message: "Network error. Please check your connection and try again.",
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top, #4E1C81 0, #4E1C81 35%, #020617 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "80px 16px 40px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 520,
          background:
            "linear-gradient(145deg, rgba(78,28,129,0.95) 0%, rgba(171,104,197,0.95) 100%)",
          borderRadius: 24,
          padding: 40,
          boxShadow: "0 30px 80px rgba(15,23,42,0.8)",
          border: "1px solid rgba(171,104,197,0.4)",
          color: "#e5e7eb",
        }}
      >
        {isVerifying ? (
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: 64,
                height: 64,
                margin: "0 auto 24px",
                border: "4px solid rgba(171,104,197,0.3)",
                borderTop: "4px solid #AB68C5",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
            <h1
              style={{
                fontSize: 28,
                fontWeight: 800,
                marginBottom: 12,
                color: "#f9fafb",
              }}
            >
              Verifying your email
            </h1>
            <p
              style={{
                fontSize: 15,
                lineHeight: 1.7,
                color: "rgba(226,232,240,0.9)",
              }}
            >
              Please wait while we verify your email address...
            </p>
          </div>
        ) : (
          <>
            <h1
              style={{
                fontSize: 28,
                fontWeight: 800,
                marginBottom: 6,
                color: "#f9fafb",
              }}
            >
              {status.type === "success"
                ? "Email verified!"
                : "Verification failed"}
            </h1>
            <p
              style={{
                fontSize: 14,
                lineHeight: 1.7,
                color: "rgba(226,232,240,0.9)",
                marginBottom: 24,
              }}
            >
              {status.type === "success"
                ? "Your email has been successfully verified. Redirecting you to login..."
                : "We couldn't verify your email address. Please try the options below."}
            </p>

            {status.type === "success" && (
              <div
                style={{
                  background: "rgba(220,252,231,0.2)",
                  color: "#dcfce7",
                  borderRadius: 12,
                  padding: "14px 16px",
                  border: "1px solid rgba(187,247,208,0.3)",
                  fontSize: 14,
                  marginBottom: 20,
                  fontWeight: 500,
                }}
              >
                {status.message}
              </div>
            )}

            {status.type === "error" && (
              <>
                <div
                  style={{
                    background: "rgba(254,226,226,0.2)",
                    color: "#fee2e2",
                    borderRadius: 12,
                    padding: "14px 16px",
                    border: "1px solid rgba(254,202,202,0.3)",
                    fontSize: 14,
                    marginBottom: 20,
                    fontWeight: 500,
                  }}
                >
                  {status.message}
                </div>

                {email && (
                  <div style={{ marginBottom: 16 }}>
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={isResending}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        borderRadius: 12,
                        border: "1px solid rgba(171,104,197,0.5)",
                        background: isResending
                          ? "rgba(148,163,184,0.3)"
                          : "rgba(171,104,197,0.2)",
                        color: "#f9fafb",
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: isResending ? "not-allowed" : "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onMouseOver={(e) => {
                        if (!isResending) {
                          e.currentTarget.style.background =
                            "rgba(171,104,197,0.35)";
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!isResending) {
                          e.currentTarget.style.background =
                            "rgba(171,104,197,0.2)";
                        }
                      }}
                    >
                      {isResending
                        ? "Sending..."
                        : "Resend Verification Email"}
                    </button>
                  </div>
                )}
              </>
            )}

            <button
              type="button"
              onClick={() => navigate("/login")}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 12,
                border: "none",
                fontSize: 15,
                fontWeight: 700,
                color: "#0b1120",
                background:
                  "linear-gradient(135deg, #4E1C81 0%, #AB68C5 100%)",
                cursor: "pointer",
                boxShadow: "0 18px 40px rgba(15,23,42,0.8)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 22px 50px rgba(15,23,42,0.9)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 18px 40px rgba(15,23,42,0.8)";
              }}
            >
              {status.type === "success" ? "Go to Login" : "Back to Login"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;

